const RECOVERY_ACTION = 'recover_meta_access';
const DEFINITIVE_REAUTH_CODES = new Set([
  'reauthorization_required',
  'meta_reauthorization_required',
  'meta_connection_reauth_required',
]);

function exactText(value) {
  return typeof value === 'string' && value.trim() ? value.trim() : '';
}

function recoveryPresentation(status, message) {
  return Object.freeze({
    action: RECOVERY_ACTION,
    status,
    message,
  });
}

export const CAMPAIGN_META_ACCESS_RECOVERY_PRESENTATIONS = Object.freeze({
  pending: recoveryPresentation(
    'pending',
    'Reading the exact sealed campaign resources and current Meta authorization. No Meta settings are being changed.',
  ),
  success: recoveryPresentation(
    'success',
    'The current Meta connection was matched to this campaign. Its saved status and settings were unchanged.',
  ),
  changed: recoveryPresentation(
    'warning',
    'Campaign details changed while access was being verified. Review the refreshed campaign before trying again; no successful verification was assumed.',
  ),
  stillBlocked: recoveryPresentation(
    'warning',
    'The refreshed campaign still requires Meta access verification. Follow the latest access guidance before trying again; campaign controls remain locked.',
  ),
  refreshUnavailable: recoveryPresentation(
    'error',
    'Campaign access was verified, but the refreshed campaign and management state could not be confirmed. Refresh status before using campaign controls.',
  ),
});

export function campaignMetaAccessRecoverySnapshot({
  campaignId,
  campaignUpdatedAt,
  companyId,
  publishIntentId,
  scope,
} = {}) {
  return Object.freeze({
    campaignId: exactText(campaignId),
    campaignUpdatedAt: exactText(campaignUpdatedAt),
    companyId: exactText(companyId),
    publishIntentId: exactText(publishIntentId),
    scope: exactText(scope),
  });
}

function validRecoverySnapshot(snapshot) {
  return snapshot
    && snapshot.campaignId
    && snapshot.campaignUpdatedAt
    && snapshot.companyId
    && snapshot.publishIntentId
    && snapshot.scope;
}

function validCurrentCampaignTimestamp(value, expectedValue) {
  const current = Date.parse(exactText(value));
  const expected = Date.parse(exactText(expectedValue));
  return Number.isFinite(current)
    && Number.isFinite(expected)
    && current >= expected;
}

function sameRecoveryContext(expected, current) {
  return current?.mounted === true
    && exactText(current.companyId) === expected.companyId
    && exactText(current.campaignId) === expected.campaignId
    && exactText(current.scope) === expected.scope;
}

function currentMetaFailureCodes(detail) {
  const provider = detail?.provider || {};
  const metrics = detail?.metrics || {};
  return [
    provider.reconciliation?.errorCode,
    provider.reconciliation?.failureCode,
    provider.failureCode,
    provider.error?.code,
    provider.errorCode,
    provider.hierarchy?.error?.code,
    provider.hierarchy?.errorCode,
    metrics.failureCode,
    metrics.error?.code,
    metrics.errorCode,
  ].map(exactText).filter(Boolean);
}

function isDefinitiveMetaAccessBlocker(code) {
  return code.startsWith('meta_campaign_credential_continuity_')
    || /^curated_campaign_(?:provider(?:_(?:hierarchy|resource))?|publish)_binding_/.test(code)
    || code === 'meta_campaign_selection_binding_invalid'
    || DEFINITIVE_REAUTH_CODES.has(code);
}

export function campaignHasCurrentMetaAccessBlocker(detail) {
  const campaign = detail?.campaign || {};
  const restoreStatus = exactText(campaign.restoreVerificationStatus) || 'not_required';
  const restoreRequiredAt = exactText(campaign.restoreVerificationRequiredAt);
  const restoreBlocked = !['not_required', 'verified'].includes(restoreStatus)
    || (Boolean(restoreRequiredAt) && restoreStatus !== 'verified');
  return restoreBlocked
    || currentMetaFailureCodes(detail).some(isDefinitiveMetaAccessBlocker);
}

export function campaignMetaAccessRecoveryStartDecision(expected, current) {
  if (!validRecoverySnapshot(expected) || !sameRecoveryContext(expected, current)) {
    return 'suppress';
  }
  if (
    exactText(current.campaignUpdatedAt) !== expected.campaignUpdatedAt
    || exactText(current.publishIntentId) !== expected.publishIntentId
    || exactText(current.metaAccessRecoveryState) !== 'available'
  ) return 'changed';
  return current.blocked === true ? 'blocked' : 'start';
}

export function campaignMetaAccessRecoveryRefetchPresentation(expected, refreshedDetail) {
  if (!validRecoverySnapshot(expected) || !refreshedDetail) {
    return CAMPAIGN_META_ACCESS_RECOVERY_PRESENTATIONS.refreshUnavailable;
  }
  if (
    exactText(refreshedDetail?.campaign?.id) !== expected.campaignId
    || exactText(refreshedDetail?.provider?.publishIntentId) !== expected.publishIntentId
    || !validCurrentCampaignTimestamp(
      refreshedDetail?.campaign?.updatedAt,
      expected.campaignUpdatedAt,
    )
    || exactText(refreshedDetail?.campaign?.status).toLowerCase() === 'archived'
    || Boolean(exactText(refreshedDetail?.campaign?.archivedAt))
  ) return CAMPAIGN_META_ACCESS_RECOVERY_PRESENTATIONS.changed;

  if (campaignHasCurrentMetaAccessBlocker(refreshedDetail)) {
    return CAMPAIGN_META_ACCESS_RECOVERY_PRESENTATIONS.stillBlocked;
  }

  // Success requires an explicit canonical null. Missing or malformed recovery
  // state is not proof that the server-side lock was cleared.
  if (refreshedDetail.metaAccessRecovery !== null) {
    return CAMPAIGN_META_ACCESS_RECOVERY_PRESENTATIONS.stillBlocked;
  }
  return CAMPAIGN_META_ACCESS_RECOVERY_PRESENTATIONS.success;
}

function safeTimestampOrNull(value) {
  if (value === null || value === undefined || value === '') return null;
  const normalized = exactText(value);
  return normalized && Number.isFinite(Date.parse(normalized)) ? normalized : undefined;
}

function normalizedRestoreVerification(value, { defaultNotRequired = false } = {}) {
  const status = exactText(value?.status).toLowerCase()
    || (defaultNotRequired ? 'not_required' : '');
  const requiredAt = safeTimestampOrNull(value?.requiredAt);
  const completedAt = safeTimestampOrNull(value?.completedAt);
  if (
    !['not_required', 'required', 'verified', 'failed'].includes(status)
    || requiredAt === undefined
    || completedAt === undefined
  ) return null;
  const coherent = status === 'not_required'
    ? requiredAt === null && completedAt === null
    : status === 'required'
      ? requiredAt !== null && completedAt === null
      : requiredAt !== null && completedAt !== null;
  return coherent ? { status, requiredAt, completedAt } : null;
}

function campaignManagementProofState(refreshedDetail, refreshedManagement, campaignId) {
  if (!refreshedDetail || !refreshedManagement || !exactText(campaignId)) return 'unavailable';
  if (
    exactText(refreshedDetail?.campaign?.status).toLowerCase() === 'archived'
    || Boolean(exactText(refreshedDetail?.campaign?.archivedAt))
  ) return 'changed';
  if (exactText(refreshedManagement.campaignId) !== exactText(campaignId)) return 'changed';
  const archivedAt = safeTimestampOrNull(refreshedManagement.archivedAt);
  if (archivedAt === undefined) return 'unavailable';
  if (archivedAt !== null) return 'changed';

  const detailRevision = refreshedDetail?.configuration?.revision;
  const managementRevision = refreshedManagement.configurationRevision;
  if (
    !Number.isSafeInteger(detailRevision)
    || detailRevision < 0
    || !Number.isSafeInteger(managementRevision)
    || managementRevision < 0
  ) return 'unavailable';
  if (detailRevision !== managementRevision) return 'changed';

  const campaign = refreshedDetail.campaign || {};
  const detailRestore = normalizedRestoreVerification({
    status: campaign.restoreVerificationStatus,
    requiredAt: campaign.restoreVerificationRequiredAt,
    completedAt: campaign.restoreVerificationCompletedAt,
  }, { defaultNotRequired: true });
  const managementRestore = normalizedRestoreVerification(
    refreshedManagement.restoreVerification,
  );
  if (!detailRestore || !managementRestore) return 'unavailable';
  if (
    ['required', 'failed'].includes(detailRestore.status)
    || ['required', 'failed'].includes(managementRestore.status)
  ) return 'blocked';
  if (
    detailRestore.status !== managementRestore.status
    || detailRestore.requiredAt !== managementRestore.requiredAt
    || detailRestore.completedAt !== managementRestore.completedAt
  ) return 'changed';
  return 'ready';
}

export function campaignManagementAllowsMetaControls(detail, management) {
  const campaignId = exactText(detail?.campaign?.id);
  return campaignManagementProofState(detail, management, campaignId) === 'ready';
}

export function campaignMetaAccessRecoveryManagementPresentation(
  expected,
  refreshedDetail,
  refreshedManagement,
) {
  if (!validRecoverySnapshot(expected)) {
    return CAMPAIGN_META_ACCESS_RECOVERY_PRESENTATIONS.refreshUnavailable;
  }
  const state = campaignManagementProofState(
    refreshedDetail,
    refreshedManagement,
    expected.campaignId,
  );
  if (state === 'ready') return CAMPAIGN_META_ACCESS_RECOVERY_PRESENTATIONS.success;
  if (state === 'blocked') return CAMPAIGN_META_ACCESS_RECOVERY_PRESENTATIONS.stillBlocked;
  if (state === 'changed') return CAMPAIGN_META_ACCESS_RECOVERY_PRESENTATIONS.changed;
  return CAMPAIGN_META_ACCESS_RECOVERY_PRESENTATIONS.refreshUnavailable;
}

function invalidRecoveryResponse() {
  return Object.assign(new Error('Campaign access verification response is invalid.'), {
    code: 'meta_campaign_access_verification_failed',
  });
}

function recoveryRequestScope(snapshot) {
  return validRecoverySnapshot(snapshot)
    ? JSON.stringify([
      snapshot.companyId,
      snapshot.campaignId,
      snapshot.campaignUpdatedAt,
      snapshot.publishIntentId,
    ])
    : '';
}

/**
 * Owns the complete confirmation/write/canonical-read sequence. The mutable
 * refs are deliberately supplied by the React owner so unmount and scope
 * cleanup can invalidate a flight without allowing a late result to render.
 */
export async function executeCampaignMetaAccessRecovery({
  expected,
  inFlight,
  versionRef,
  requestConfirmation,
  readCurrentContext,
  postRecovery,
  refreshDetail,
  refreshManagement,
  present,
  safeFailure,
} = {}) {
  const requestScope = recoveryRequestScope(expected);
  if (
    !requestScope
    || !inFlight
    || typeof inFlight !== 'object'
    || exactText(inFlight.current)
    || !versionRef
    || typeof versionRef !== 'object'
  ) return Object.freeze({ state: 'ignored', reason: requestScope ? 'busy' : 'invalid' });

  inFlight.current = requestScope;
  const version = Number(versionRef.current || 0) + 1;
  versionRef.current = version;
  const claimIsCurrent = () => (
    inFlight.current === requestScope
    && versionRef.current === version
  );
  const contextIsCurrent = () => (
    claimIsCurrent()
    && sameRecoveryContext(expected, readCurrentContext?.())
  );

  try {
    const approved = await requestConfirmation?.();
    if (!approved) return Object.freeze({ state: 'cancelled' });
    if (!claimIsCurrent()) return Object.freeze({ state: 'ignored', reason: 'superseded' });

    const decision = campaignMetaAccessRecoveryStartDecision(
      expected,
      readCurrentContext?.(),
    );
    if (decision === 'suppress') {
      return Object.freeze({ state: 'ignored', reason: 'context_changed' });
    }
    if (decision === 'blocked') {
      return Object.freeze({ state: 'ignored', reason: 'competing_action' });
    }
    if (decision === 'changed') {
      present?.(CAMPAIGN_META_ACCESS_RECOVERY_PRESENTATIONS.changed);
      return Object.freeze({ state: 'unverified', reason: 'markers_changed' });
    }

    present?.(CAMPAIGN_META_ACCESS_RECOVERY_PRESENTATIONS.pending);
    const response = await postRecovery?.({
      expectedCampaignUpdatedAt: expected.campaignUpdatedAt,
      expectedPublishIntentId: expected.publishIntentId,
    });
    if (!contextIsCurrent()) {
      return Object.freeze({ state: 'ignored', reason: 'context_changed' });
    }
    if (
      exactText(response?.campaignId) !== expected.campaignId
      || exactText(response?.metaAccessRecovery?.state) !== 'verified'
    ) throw invalidRecoveryResponse();

    const refreshedDetail = await refreshDetail?.();
    if (!contextIsCurrent()) {
      return Object.freeze({ state: 'ignored', reason: 'context_changed' });
    }
    const refreshedManagement = await refreshManagement?.();
    if (!contextIsCurrent()) {
      return Object.freeze({ state: 'ignored', reason: 'context_changed' });
    }

    let presentation = campaignMetaAccessRecoveryRefetchPresentation(
      expected,
      refreshedDetail,
    );
    if (presentation.status === 'success') {
      presentation = campaignMetaAccessRecoveryManagementPresentation(
        expected,
        refreshedDetail,
        refreshedManagement,
      );
    }
    present?.(presentation);
    return Object.freeze({
      state: presentation.status === 'success' ? 'verified' : 'unverified',
      reason: presentation.status === 'success' ? '' : 'canonical_proof_missing',
    });
  } catch (error) {
    if (!contextIsCurrent()) {
      return Object.freeze({ state: 'ignored', reason: 'context_changed' });
    }
    present?.(recoveryPresentation(
      'error',
      typeof safeFailure === 'function'
        ? safeFailure(error)
        : 'Campaign access could not be verified safely. Try again shortly.',
    ));
    return Object.freeze({ state: 'error' });
  } finally {
    if (inFlight.current === requestScope) inFlight.current = '';
  }
}
